import {
  BoundMultiSelectField,
  BoundNumberField,
  BoundSelectField,
  BoundTextField,
  Button,
  Css,
  FormSection,
  PageHeaderLayout,
  useComputed,
  useSnackbar,
} from "@homebound/beam";
import { ObjectConfig, required, useFormState } from "@homebound/form-state";
import { Navigate, useNavigate } from "react-router-dom";
import { createPlanPackageCreateUrl, createPlanPackagesUrl } from "src/RouteUrls";
import { Loading } from "src/components/Loading";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { useUserFeatureFlagsQuery } from "src/contexts/FeatureFlags/FeatureFlagContext.generated";
import {
  FeatureFlagType,
  Maybe,
  ReadyPlanStatus,
  ReadyPlanType,
  SavePlanPackageInput,
} from "src/generated/graphqlTypes";
import { useMarketFilter } from "src/hooks";
import { useCreatePlanPackageMutation } from "src/routes/libraries/plan-package-v2/create/CreatePlanPackagePage.generated";
import { planPackageOverviewRoute } from "src/routing/domains/librariesPaths";

export function CreatePlanPackagePage() {
  const isPlanPackagesV2Enabled = useFeatureFlag(FeatureFlagType.PlanPackagesV2);
  const { loading } = useUserFeatureFlagsQuery();
  if (loading) return <Loading />;
  if (!isPlanPackagesV2Enabled) return <Navigate to={createPlanPackageCreateUrl()} replace />;
  return <CreatePlanPackageView />;
}

function CreatePlanPackageView() {
  const navigate = useNavigate();
  const { triggerNotice } = useSnackbar();
  const { options: markets } = useMarketFilter();
  const [create] = useCreatePlanPackageMutation();
  const formState = useFormState({ config: formConfig });
  const canCreate = useComputed(() => formState.valid, [formState]);

  async function onCreate() {
    const { code, ...rest } = formState.value;
    const { data } = await create({
      variables: { input: { ...rest, status: ReadyPlanStatus.Draft, code: code!.toString().padStart(3, "0") } },
    });
    const { id, name, version } = data!.savePlanPackage.planPackage;
    triggerNotice({ message: `Plan package: ${name} successfully created`, icon: "success" });
    navigate(planPackageOverviewRoute.url({ planPackageId: id, planPackageVersionId: version.id }));
  }

  return (
    <PageHeaderLayout
      pageHeader={{
        title: "Create a Plan Package",
        breadcrumbs: { breadcrumbs: { label: "Plan Library", href: createPlanPackagesUrl() } },
        rightSlot: (
          <div css={Css.df.aic.gap1.$}>
            <Button label="Cancel" variant="tertiary" onClick={createPlanPackagesUrl()} />
            <Button label="Create" onClick={onCreate} disabled={!canCreate} />
          </div>
        ),
      }}
    >
      <div css={Css.pt5.mxa.w50.df.fdc.gap4.$}>
        <FormSection
          title="Plan Name"
          fields={<BoundTextField label="Plan Name" field={formState.name} placeholder="i.e. The Everett" fullWidth />}
        />
        <FormSection
          title="Plan Code"
          description="Choose a plan type and enter a plan number."
          fields={
            <div css={Css.dg.gtc("130px 1fr").gap2.$}>
              <BoundSelectField
                label="Plan Type"
                field={formState.type}
                options={Object.values(ReadyPlanType).map((code) => ({ id: code, name: code }))}
                fullWidth
              />
              <BoundNumberField
                label="Plan Number"
                field={formState.code}
                placeholder="Plan #"
                numIntegerDigits={4}
                useGrouping={false}
                fullWidth
              />
            </div>
          }
        />
        <FormSection
          title="Market"
          description="Select the markets where this plan will be available to create a product offering."
          fields={<BoundMultiSelectField label="Market(s)" field={formState.markets} options={markets} fullWidth />}
        />
      </div>
    </PageHeaderLayout>
  );
}

/** `code` is a `String` on the input, but the design edits it as a plain number. */
type CreatePlanPackageForm = Pick<SavePlanPackageInput, "name" | "type" | "markets"> & { code: Maybe<number> };

const formConfig: ObjectConfig<CreatePlanPackageForm> = {
  name: { type: "value", rules: [required] },
  type: { type: "value", rules: [required] },
  code: { type: "value", rules: [required] },
  markets: { type: "value" },
};
